/**
 * The one Convex document-id shape check (`13` §4.1). Pure leaf (type-only
 * imports), so the app imports it too.
 *
 * Replaces `logic/tasks/convex_id_format.ts` (no trim), `isValidConvexId` in
 * `logic/shared/convex_id_format.ts` and `logic/scheduling/rest_ids.ts`, and
 * the private copies in e-mail threading, comment mentions and the app.
 */

import type { Id, TableNames } from "../../convex/_generated/dataModel";
import type { QueryCtx } from "../../convex/_generated/server";

const CONVEX_ID_RE = /^[a-z0-9]{32}$/;

/**
 * True when `value`, surrounding whitespace ignored, has the shape of a Convex
 * document id: 32 lowercase letters or digits. Use the trimmed value after
 * the check.
 */
export function looksLikeConvexId(value: string): boolean {
  return CONVEX_ID_RE.test(value.trim());
}

type IdNormalizingDb = Pick<QueryCtx["db"], "normalizeId">;

/**
 * The trimmed raw id when it encodes a document of `table` (not merely the
 * 32-char shape), else null.
 */
export function normalizeTableId<TTable extends TableNames>(
  db: IdNormalizingDb,
  table: TTable,
  raw: string,
): Id<TTable> | null {
  const trimmed: string = raw.trim();
  if (!looksLikeConvexId(trimmed)) return null;
  return db.normalizeId(table, trimmed);
}
